import { ApolloQueryResult } from "@apollo/client/core";
import { computed, reactive, ref, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  validateStartTimeAfterNow,
  validateStartTimeAfterDeliveryStart,
  validateStartTimeBeforeDeliveryEnd,
} from "@/common/deliveryTimeValidationUtil";
import type { AreaItem, DeliveryAvailabilityData } from "../types";
import { WEEKS } from "../constants";
import {
  DeliveryRequestUpdateInput,
  ListDeliveryAvailabilityDocument,
  ListDeliveryAvailabilityQuery,
} from "@/generated/graphql";

type DeliveryStartMinit = { label: string; deliveryStartMinit: number };

interface BeforData {
  delivery_date: string;
  delivery_id: number;
  irregularDeliveryStartTime: string[];
}

const normalizeTimeParts = (time?: string | null): [string, string] => {
  const [hour = "00", minute = "00"] = (time ?? "").split(":");
  return [hour.padStart(2, "0"), minute.padStart(2, "0")];
};

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestDeliveryTimeParams {
  listDeliveryArea: Ref<
    | Array<{ __typename?: "DeliveryArea" | undefined; area_code: string; area_name: string; acceptable_end_time: string } | null | undefined>
    | null
    | undefined
  >;
  targetShopCode: Ref<string>;
  editDeliveryRequest: Ref<DeliveryRequestUpdateInput | null>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  deliveryAreaName: Ref<string>;
  acceptableEndTime: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  sectionBlockList: Ref<
    | {
      __typename?: "Kana_Step_A" | undefined;
      line_a?: (string | null | undefined)[] | null | undefined;
      line_ka?: (string | null | undefined)[] | null | undefined;
      line_sa?: (string | null | undefined)[] | null | undefined;
      line_ta?: (string | null | undefined)[] | null | undefined;
      line_na?: (string | null | undefined)[] | null | undefined;
      line_ha?: (string | null | undefined)[] | null | undefined;
      line_ma?: (string | null | undefined)[] | null | undefined;
      line_ya?: (string | null | undefined)[] | null | undefined;
      line_ra?: (string | null | undefined)[] | null | undefined;
      line_wa?: (string | null | undefined)[] | null | undefined;
      line_other?: (string | null | undefined)[] | null | undefined;
    }
    | null
    | undefined
  >;
  blockList: Ref<Array<string | null | undefined> | null | undefined>;
  originalCityAddress: Ref<string>;
  originalSectionAddress: Ref<string>;
  zipCodeFromPref: Ref<string>;
  displayDeliveryTime: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  delivery_type: Ref<number | null | undefined>;
  doubleDeliOriconCount: Ref<number | null | undefined>;
  charterDeliOriconCount: Ref<number | null | undefined>;
  wordList: Record<string, string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestDeliveryTimeState = () => {
  const acceptableEndTime = ref("");
  const deliveryId_0_Flg = ref(false);
  const overDeliveryEndTime = ref(false);
  const doubleDeliOriconCount = ref<number | null | undefined>(null);
  const isDoubleDeli = ref(false);
  const pre_double_deli_oricon_flg = ref<number | null | undefined>(0);
  const charterDeliOriconCount = ref<number | null | undefined>(null);
  const isCharterDeli = ref(false);
  const pre_charter_deli_oricon_flg = ref<number | null | undefined>(0);
  const delivery_type = ref<number | null | undefined>(0);
  const deliveryAvailabilityHeader = ref<Map<string, string>>(new Map());
  const deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const disp_deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const displayDeliveryTime = ref("");
  const targetShopCode = ref("");

  return {
    acceptableEndTime,
    deliveryId_0_Flg,
    overDeliveryEndTime,
    doubleDeliOriconCount,
    isDoubleDeli,
    pre_double_deli_oricon_flg,
    charterDeliOriconCount,
    isCharterDeli,
    pre_charter_deli_oricon_flg,
    delivery_type,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    displayDeliveryTime,
    targetShopCode,
  };
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestDeliveryTime = ({
  listDeliveryArea,
  targetShopCode,
  editDeliveryRequest,
  errdialog,
  errMessage,
  deliveryAreaName,
  acceptableEndTime,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  sectionBlockList,
  blockList,
  originalCityAddress,
  originalSectionAddress,
  zipCodeFromPref,
  displayDeliveryTime,
  deliveryId_0_Flg,
  delivery_type,
  doubleDeliOriconCount,
  charterDeliOriconCount,
  wordList,
}: UseDeliveryRequestDeliveryTimeParams) => {
  const getRequiredShopCode = (): string => {
    const shopCode = localStorageGetShopCode();
    if (!shopCode) {
      throw new Error("店舗コードが取得できません。再ログインしてください。");
    }
    return shopCode;
  };

  const deliveryStartHourItem = ref<number[]>([]);
  const deliveryStartHourValues = ref<number | null | undefined>(undefined);
  const deliveryStartMinitItem = ref<DeliveryStartMinit[]>([
    { label: "00", deliveryStartMinit: 0 },
    { label: "30", deliveryStartMinit: 1 },
  ]);
  const deliveryStartMinitValues = ref<DeliveryStartMinit | null | undefined>(
    undefined
  );
  const befor_data = reactive<BeforData>({
    delivery_date: "",
    delivery_id: 999,
    irregularDeliveryStartTime: [],
  });
  const irregularDeliveryStartTime = ref("");
  const irregularDeliveryEndTime = ref("");
  const deliveryStartTime = ref("");
  const deliveryEndTime = ref("");
  const deliverySelectDayBox = ref("");
  const deliverySelectBox = ref("");
  const formatDate = (date: string): string => {
    let _date;
    let retDate = "";
    if (date) {
      _date = new Date(date);
      retDate =
        _date.getMonth() +
        1 +
        "/" +
        _date.getDate() +
        "(" +
        WEEKS[_date.getDay()] +
        ")";
    }
    return retDate;
  };
  const isDispDeliMessage = (dispFlg: boolean): boolean => {
    if (dispFlg === false || delivery_type.value == 3) {
      return false;
    }
    return true;
  };
  const doubleDeliCheckLabel = computed<string>(() => {
    return (wordList.lbl_double_entry_chk ?? "").replace(
      "{{n}}",
      (doubleDeliOriconCount.value ?? 0).toString()
    );
  });
  const charterDeliChkLabel = computed<string>(() => {
    return (wordList.lbl_charter_chk ?? "").replace(
      "{{n}}",
      (charterDeliOriconCount.value ?? 0).toString()
    );
  });

  /**
   * 目的: 都度便の開始時刻表示(`irregularDeliveryStartTime`)を確定する。
   * 利用方法: 便選択時、または時/分の変更後に呼び出す。
   */
  const setDeliveryStartTime = async (): Promise<void> => {
    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      (deliveryStartMinitValues.value === undefined ||
        deliveryStartMinitValues.value === null)
    ) {
      // 「時のみ選択」は未確定扱いにして保存時バリデーションへ委ねる。
      irregularDeliveryStartTime.value = "";
      return;
    }

    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      deliveryStartMinitValues.value !== undefined &&
      deliveryStartMinitValues.value !== null
    ) {
      irregularDeliveryStartTime.value =
        String(deliveryStartHourValues.value).padStart(2, "0") +
        ":" +
        deliveryStartMinitValues.value.label;
    } else {
      let hour = 0;
      let minut = 0;

      const beforeStartHour = Number(befor_data.irregularDeliveryStartTime[0]);
      const beforeStartMinute = Number(befor_data.irregularDeliveryStartTime[1]);
      const isBeforeDeliveryId =
        editDeliveryRequest.value?.delivery_id === befor_data.delivery_id;

      if (
        isBeforeDeliveryId &&
        befor_data.irregularDeliveryStartTime[1] !== "00" &&
        befor_data.irregularDeliveryStartTime[1] !== "30" &&
        !Number.isNaN(beforeStartHour) &&
        !Number.isNaN(beforeStartMinute)
      ) {
        hour = beforeStartHour;
        minut = beforeStartMinute;
      } else {
        if (
          now.getHours() < Number(deliveryStartTimeAry[0]) ||
          (now.getHours() == Number(deliveryStartTimeAry[0]) &&
            now.getMinutes() < Number(deliveryStartTimeAry[1]))
        ) {
          hour = Number(deliveryStartTimeAry[0]);
          minut = Number(deliveryStartTimeAry[1]);
        } else if (now.getMinutes() < 30) {
          hour = now.getHours();
          minut = 30;
        } else {
          hour = now.getHours() + 1;
          minut = 0;
        }
      }
      irregularDeliveryStartTime.value =
        String(hour).padStart(2, "0") + ":" + String(minut).padStart(2, "0");
    }
  };

  /**
   * 目的: 都度便の終了時刻(`irregularDeliveryEndTime`)を「開始から3時間以内」かつ受付終了時刻内で確定する。
   * 利用方法: `setDeliveryStartTime` の後に呼び出し、開始時刻に追従して終了時刻を更新する。
   */
  const setDeliveryEndTime = async (): Promise<void> => {
    // 開始時刻未確定時は終了時刻も未確定にする。
    if (irregularDeliveryStartTime.value.length === 0) {
      irregularDeliveryEndTime.value = "";
      return;
    }
    const deliveryStartTimeAry = irregularDeliveryStartTime.value.split(":");
    const deliveryEndTimeAry = deliveryEndTime.value.split(":");
    const endTime = Number(deliveryStartTimeAry[0]) + 3;
    if (
      endTime > Number(deliveryEndTimeAry[0]) ||
      (endTime == Number(deliveryEndTimeAry[0]) &&
        deliveryStartTimeAry[1] > deliveryEndTimeAry[1])
    ) {
      irregularDeliveryEndTime.value = deliveryEndTime.value;
    } else {
      irregularDeliveryEndTime.value =
        String(endTime).padStart(2, "0") + ":" + deliveryStartTimeAry[1];
    }
  };

  /**
   * 目的: 当日配送時に、入力した希望時刻が現在時刻より後かを判定する。
   * 利用方法: 時刻(時・分)変更時のバリデーション内で呼び出す。
   */
  const chkDeliveryStartTime_Now = async (): Promise<boolean> => {
    return validateStartTimeAfterNow(
      editDeliveryRequest.value!.required_delivery_date ?? "",
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  /**
   * 目的: 入力した希望時刻が、選択中の便の配達開始時刻以降かを判定する。
   * 利用方法: 時刻(時・分)変更時のバリデーション内で呼び出す。
   */
  const chkDeliveryStartTime = async (): Promise<boolean> => {
    return validateStartTimeAfterDeliveryStart(
      deliveryStartTime.value,
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  /**
   * 目的: 入力した希望時刻が受付終了時刻を超過していないかを判定する。
   * 利用方法: 時刻(時・分)変更時のバリデーション内で呼び出す。
   */
  const chkDeliveryEndTimeOver = async (): Promise<boolean> => {
    return validateStartTimeBeforeDeliveryEnd(
      deliveryEndTime.value,
      deliveryStartHourValues.value,
      deliveryStartMinitValues.value?.label
    );
  };

  /**
   * 目的: 希望時刻の「時」変更時にバリデーションを実施し、選択可能な分候補と開始/終了時刻を再計算する。
   * 利用方法: 時刻「時」のプルダウン変更イベントから呼び出す。
   */
  const onChangeDeliveryStartHour = async (): Promise<void> => {
    if (deliveryStartHourValues.value !== undefined) {
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value = "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryStartTime())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }

      const now = new Date();
      const deliveryStartTimeAry = deliveryStartTime.value.split(":");
      const endTimeAry = acceptableEndTime.value.split(":");
      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      if (
        deliveryStartHourValues.value !== undefined &&
        deliveryStartHourValues.value !== null
      ) {
        if (
          (deliveryStartHourValues.value == now.getHours() &&
            now.getMinutes() < 30) ||
          (deliveryStartHourValues.value == Number(deliveryStartTimeAry[0]) &&
            deliveryStartTimeAry[1] == "30") ||
          (editDeliveryRequest.value!.required_delivery_date == befor_data.delivery_date &&
            editDeliveryRequest.value!.delivery_id == befor_data.delivery_id &&
            deliveryStartHourValues.value ==
              Number(befor_data.irregularDeliveryStartTime[0]) &&
            befor_data.irregularDeliveryStartTime[1] == "30")
        ) {
          deliveryStartMinitItem.value.shift();
          deliveryStartMinitValues.value = {
            label: "30",
            deliveryStartMinit: 1,
          };
        } else if (
          deliveryStartHourValues.value == Number(endTimeAry[0]) &&
          endTimeAry[1] == "00"
        ) {
          deliveryStartMinitItem.value.pop();
          deliveryStartMinitValues.value = {
            label: "00",
            deliveryStartMinit: 0,
          };
        }
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  };

  /**
   * 目的: 希望時刻の「分」変更時にバリデーションを実施し、開始/終了時刻を再計算する。
   * 利用方法: 時刻「分」のプルダウン変更イベントから呼び出す。
   */
  const onChangeDeliveryStartMinit = async (): Promise<void> => {
    if (deliveryStartMinitValues.value !== undefined) {
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value = "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryStartTime())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  };

  /**
   * 目的: ユーザーが選択した配達便/日付を編集データへ反映し、都度便(0便)選択時の時刻入力候補を初期化する。
   * 利用方法: 配達枠セルのクリックイベントから呼び出す。
   */
  const onDeliveryAvailabilityClick = async (
    date: string,
    deliveryid: string,
    delivery_start_time: string,
    delivery_end_time: string,
    deliverySelectDay: string,
    deliverySelect: string
  ): Promise<void> => {
    editDeliveryRequest.value!.delivery_id = Number(deliveryid);
    editDeliveryRequest.value!.required_delivery_date = date;

    let strDeliveryId = "";
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;

    if (editDeliveryRequest.value!.delivery_id === 0) {
      let startHour = 0;
      const now = new Date();
      const nowDate =
        now.getFullYear() +
        "-" +
        String(now.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getDate()).padStart(2, "0");
      const deliveryStartTimeAry = delivery_start_time.split(":");

      if (
        date !== nowDate ||
        (date == nowDate &&
          (Number(deliveryStartTimeAry[0]) > now.getHours() ||
            (Number(deliveryStartTimeAry[0]) == now.getHours() &&
              Number(deliveryStartTimeAry[1]) > now.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else if (now.getMinutes() < 30) {
        startHour = now.getHours();
      } else {
        startHour = now.getHours() + 1;
      }
      const endTimeAry = acceptableEndTime.value.split(":");
      for (let i = startHour; i <= Number(endTimeAry[0]); i++) {
        deliveryStartHourItem.value.push(i);
      }

      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      const beforeStartHour = Number(befor_data.irregularDeliveryStartTime[0]);
      const beforeStartMinute = befor_data.irregularDeliveryStartTime[1];
      if (
        Number(deliveryid) == befor_data.delivery_id &&
        (beforeStartMinute == "00" || beforeStartMinute == "30") &&
        !Number.isNaN(beforeStartHour)
      ) {
        if (!deliveryStartHourItem.value.includes(beforeStartHour)) {
          deliveryStartHourItem.value.push(beforeStartHour);
          deliveryStartHourItem.value.sort((a, b) => a - b);
        }
        deliveryStartHourValues.value = beforeStartHour;
        deliveryStartMinitValues.value = {
          label: beforeStartMinute,
          deliveryStartMinit: beforeStartMinute == "00" ? 0 : 1,
        };
      }

      strDeliveryId = "都度便";
      deliveryId_0_Flg.value = true;

      deliveryStartTime.value = delivery_start_time;
      deliveryEndTime.value = delivery_end_time;

      await setDeliveryStartTime();
      await setDeliveryEndTime();

      editDeliveryRequest.value!.irregular_delivery_start_time = undefined;
      editDeliveryRequest.value!.irregular_delivery_end_time = undefined;
    } else {
      strDeliveryId = `${editDeliveryRequest.value!.delivery_id}便`;
    }

    displayDeliveryTime.value =
      editDeliveryRequest.value!.required_delivery_date.substring(0, 10) +
      "  " +
      strDeliveryId;
    deliverySelectDayBox.value = deliverySelectDay;
    deliverySelectBox.value = deliverySelect;
  };

  const initializeEditDeliveryTime = async (requiredDate: string): Promise<string> => {
    if (!editDeliveryRequest.value) {
      return "";
    }

    let strDeliveryId = "";
    deliveryId_0_Flg.value = false;
    let deliveryStartTimeAry: string[] = [];
    let deliveryEndTimeAry: string[] = [];

    if (editDeliveryRequest.value.delivery_id === 0) {
      const today = new Date().toLocaleDateString("sv-SE");
      const checkDate = today <= requiredDate ? requiredDate : today;
      const currentDeliveryId = editDeliveryRequest.value.delivery_id;

      const matchedAvailability = disp_deliveryAvailabilityData.value.find((value) => {
        const head = value?.[0];
        return !!head && head.delivery_date === checkDate && head.delivery_id === currentDeliveryId;
      });

      const [resolvedStartHour, resolvedStartMinute] = normalizeTimeParts(
        matchedAvailability?.[0]?.delivery_start_time ??
          editDeliveryRequest.value.irregular_delivery_start_time
      );
      const [resolvedEndHour, resolvedEndMinute] = normalizeTimeParts(
        matchedAvailability?.[0]?.delivery_end_time ??
          editDeliveryRequest.value.irregular_delivery_end_time ??
          acceptableEndTime.value
      );
      deliveryStartTimeAry = [resolvedStartHour, resolvedStartMinute];
      deliveryEndTimeAry = [resolvedEndHour, resolvedEndMinute];

      strDeliveryId = "都度便";
      deliveryId_0_Flg.value = true;

      deliveryStartTime.value =
        deliveryStartTimeAry[0] +
        ":" +
        deliveryStartTimeAry[1];
      deliveryEndTime.value =
        deliveryEndTimeAry[0] +
        ":" +
        deliveryEndTimeAry[1];

      let startHour = 0;
      const nowLocal = new Date();
      const nowDateStr =
        nowLocal.getFullYear() +
        "-" +
        String(nowLocal.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(nowLocal.getDate()).padStart(2, "0");
      const endTimeAry = normalizeTimeParts(acceptableEndTime.value);

      if (
        requiredDate !== nowDateStr ||
        (requiredDate === nowDateStr &&
          (Number(deliveryStartTimeAry[0]) > nowLocal.getHours() ||
            (Number(deliveryStartTimeAry[0]) === nowLocal.getHours() &&
              Number(deliveryStartTimeAry[1]) > nowLocal.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else {
        startHour = nowLocal.getMinutes() < 30 ? nowLocal.getHours() : nowLocal.getHours() + 1;
      }

      const endHour = Number(endTimeAry[0]);
      for (let i = startHour; i <= endHour; i++) {
        deliveryStartHourItem.value.push(i);
      }

      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      befor_data.delivery_date = editDeliveryRequest.value.required_delivery_date ?? "";
      befor_data.delivery_id = editDeliveryRequest.value.delivery_id ?? 0;
      befor_data.irregularDeliveryStartTime = (
        editDeliveryRequest.value.irregular_delivery_start_time ?? "00:00"
      ).split(":");

      const now_minut = befor_data.irregularDeliveryStartTime[1];

      if (now_minut === "00" || now_minut === "30") {
        if (Number(befor_data.irregularDeliveryStartTime[0]) < startHour) {
          deliveryStartHourItem.value.unshift(Number(befor_data.irregularDeliveryStartTime[0]));
        }
        deliveryStartHourValues.value = Number(befor_data.irregularDeliveryStartTime[0]);
        if (befor_data.irregularDeliveryStartTime[1] === "30") {
          deliveryStartMinitItem.value.shift();
        }
        deliveryStartMinitItem.value.forEach((v) => {
          if (v.label === befor_data.irregularDeliveryStartTime[1]) {
            deliveryStartMinitValues.value = {
              label: v.label,
              deliveryStartMinit: v.deliveryStartMinit,
            };
          }
        });
      }

      irregularDeliveryStartTime.value = "";
      irregularDeliveryEndTime.value = "";
      await setDeliveryStartTime();
      await setDeliveryEndTime();
    } else {
      strDeliveryId = `${editDeliveryRequest.value.delivery_id ?? ""}便`;
    }

    return strDeliveryId;
  };

  const onClickListDeliveryArea = async (item: AreaItem): Promise<void> => {
    deliveryAvailabilityHeader.value = new Map();
    disp_deliveryAvailabilityData.value = [];
    let addAry: DeliveryAvailabilityData[] = [];

    if (!editDeliveryRequest.value) {
      return;
    }

    editDeliveryRequest.value.area_code = item.area_code;
    deliveryAreaName.value = item.area_name;
    acceptableEndTime.value = item.acceptable_end_time;

    deliveryAvailabilityData.value.forEach(
      (listVal: DeliveryAvailabilityData[]) => {
        addAry = [];
        listVal.forEach((value) => {
          if (String(value.area_code) == String(item.area_code)) {
            addAry.push(value);
            if (value.delivery_id_desc !== "") {
              deliveryAvailabilityHeader.value.set(
                value.delivery_id_desc,
                value.delivery_id_desc
              );
            }

            if (listVal[0].delivery_id == 0) {
              deliveryId_0_Flg.value = true;
              deliveryStartTime.value = listVal[0].delivery_start_time;
            } else {
              deliveryId_0_Flg.value = false;
              deliveryStartTime.value = "";
            }
          }
        });

        if (addAry.length > 0) {
          disp_deliveryAvailabilityData.value.push(addAry);
        }
      }
    );
  };

  const getDeliveryTimeList = async (chain_code: string): Promise<void> => {
    if (!listDeliveryArea.value || listDeliveryArea.value.length === 0) {
      return;
    }

    try {
      const apolloClient = await generateApolloClient();

      let result: ApolloQueryResult<ListDeliveryAvailabilityQuery>;
      let list: NonNullable<ListDeliveryAvailabilityQuery["listDeliveryAvailability"]> = [];

      for (let i = 0; i < listDeliveryArea.value.length; i++) {
        const shopCode =
          targetShopCode.value !== ""
            ? targetShopCode.value
            : getRequiredShopCode();
        result = await apolloClient.query<ListDeliveryAvailabilityQuery>({
          query: ListDeliveryAvailabilityDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chain_code,
            shop_code: shopCode,
            area_code: listDeliveryArea.value[i]?.area_code,
            member_id: editDeliveryRequest.value?.member_id ?? null,
          },
          fetchPolicy: "no-cache",
        });

        list = result.data.listDeliveryAvailability
          ? JSON.parse(JSON.stringify(result.data.listDeliveryAvailability))
          : [];
        if (list === undefined || list === null || list.length <= 0) {
          errdialog.value = true;
          errMessage.value = "選択した住所で配達可能な時間帯はありません。";
          deliveryAreaName.value = "";
          deliveryAvailabilityData.value = [];
          disp_deliveryAvailabilityData.value = [];
          sectionBlockList.value = null;
          blockList.value = [];
          originalCityAddress.value = "";
          originalSectionAddress.value = "";
          return;
        }

        let defaultAreaCode = "";
        let currentDate = "";
        let currentAvialibity: DeliveryAvailabilityData[] = [];
        let idx = 0;
        let addDeliveryIdAry: number[] = [];
        let addCnt = 0;
        let current_area_code = "";

        addDeliveryIdAry = list.map(function (item) {
          return item!["delivery_id"];
        });
        addDeliveryIdAry = Array.from(new Set(addDeliveryIdAry));

        list.forEach((value) => {
          if (value !== undefined && value !== null) {
            if (defaultAreaCode.length == 0) {
              defaultAreaCode = value.area_code;
            }
            if (value.area_code !== defaultAreaCode) {
              return;
            }

            current_area_code = listDeliveryArea.value?.[i]?.area_code ?? "";
            value.delivery_date = value.delivery_date.substring(0, 10);
            if (currentDate.length == 0 || currentDate != value.delivery_date) {
              currentDate = value.delivery_date;
              if (currentAvialibity.length > 0) {
                if (addCnt < addDeliveryIdAry.length) {
                  for (let i = addCnt; i < addDeliveryIdAry.length; i++) {
                    currentAvialibity.push({
                      key: ++idx,
                      area_code: current_area_code,
                      delivery_id: addDeliveryIdAry[i],
                      delivery_id_desc: "",
                      delivery_date: value.delivery_date,
                      delivery_start_time: "",
                      delivery_end_time: "",
                      availability: "×",
                      already_requested: 0,
                    });
                  }
                }

                deliveryAvailabilityData.value.push(currentAvialibity);
                if (current_area_code === editDeliveryRequest.value?.area_code) {
                  disp_deliveryAvailabilityData.value.push(currentAvialibity);
                }
              }
              currentAvialibity = [];
              idx = 0;
              addCnt = 0;
            }

            for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
              if (addDeliveryIdAry[j] !== value.delivery_id) {
                currentAvialibity.push({
                  key: ++idx,
                  area_code: current_area_code,
                  delivery_id: addDeliveryIdAry[j],
                  delivery_id_desc: "",
                  delivery_date: value.delivery_date,
                  delivery_start_time: "",
                  delivery_end_time: "",
                  availability: "×",
                  already_requested: 0,
                });
              } else {
                addCnt = j;
                break;
              }
            }

            currentAvialibity.push({
              key: ++idx,
              area_code: current_area_code,
              delivery_id: value.delivery_id,
              delivery_id_desc: value.delivery_id_desc,
              delivery_date: value.delivery_date,
              delivery_start_time: value.delivery_start_time,
              delivery_end_time: value.delivery_end_time,
              availability: value.availability.trim(),
              already_requested: value.already_requested
                ? value.already_requested
                : 0,
            });
            addCnt++;
          }
        });

        if (currentAvialibity.length > 0) {
          if (addCnt < addDeliveryIdAry.length) {
            for (let i = addCnt; i < addDeliveryIdAry.length; i++) {
              currentAvialibity.push({
                key: ++idx,
                area_code: current_area_code,
                delivery_id: addDeliveryIdAry[i],
                delivery_id_desc: "",
                delivery_date: currentDate,
                delivery_start_time: "",
                delivery_end_time: "",
                availability: "×",
                already_requested: 0,
              });
            }
          }
          deliveryAvailabilityData.value.push(currentAvialibity);
          if (current_area_code === editDeliveryRequest.value?.area_code) {
            disp_deliveryAvailabilityData.value.push(currentAvialibity);
          }
        }

        if (current_area_code === editDeliveryRequest.value?.area_code) {
          if (editDeliveryRequest.value) {
            editDeliveryRequest.value.delivery_zip_code = zipCodeFromPref.value;
            if (originalCityAddress.value != "") {
              editDeliveryRequest.value.delivery_id = 0;
              editDeliveryRequest.value.required_delivery_date = "";
              displayDeliveryTime.value = "";
            }
          }
        }
        list = [];
      }

      if (listDeliveryArea.value && listDeliveryArea.value.length == 1) {
        if (deliveryAvailabilityData.value[0]?.[0]?.delivery_id == 0) {
          deliveryId_0_Flg.value = true;
          deliveryStartTime.value =
            deliveryAvailabilityData.value[0][0].delivery_start_time;
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        const originalMessage = err.message;
        err.message = `選択した住所は配達エリア外です。(${originalMessage})`;
        throw err;
      }
      throw new Error("選択した住所は配達エリア外です。");
    }
  };

  return {
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitItem,
    deliveryStartMinitValues,
    befor_data,
    irregularDeliveryStartTime,
    irregularDeliveryEndTime,
    deliveryStartTime,
    deliveryEndTime,
    deliverySelectDayBox,
    deliverySelectBox,
    formatDate,
    isDispDeliMessage,
    doubleDeliCheckLabel,
    charterDeliChkLabel,
    onClickListDeliveryArea,
    onDeliveryAvailabilityClick,
    onChangeDeliveryStartHour,
    onChangeDeliveryStartMinit,
    initializeEditDeliveryTime,
    setDeliveryStartTime,
    setDeliveryEndTime,
    getDeliveryTimeList,
  };
};
